import React, { useState } from 'react';
import { Modal, Button, InputNumber, Tooltip, Select, Input } from 'antd';
import Divider from 'antd/es/divider';
import { CheckOutlined, FastBackwardOutlined, FastForwardOutlined, DeleteRowOutlined, SaveOutlined } from '@ant-design/icons';
import { Ticket, AF_START_ITERATION, AF_TITLE, AF_PLANNING_PRIORITY, AF_INITIATIV, AF_PLANNING_STATUS, AF_END_ITERATION } from '../../services/Ticket';
import { findNextQuarterStart, findPrevQuarterStart } from '../../utils/DateUtils';
import { ADO_PLANNING_STATUSES } from '../../Common/Constants';
import moment from 'moment';

interface ChartMenuMultiSelectProps {
  showMultiSelectMenu: boolean;
  selectedTickets: Ticket[];
  onSetRefresh: () => void;
  handleClose: () => void;
  onMoveAndExtendIteration?: (...args: any[]) => void;
  RemoveFromTimeline: (ticketId: number) => void;
  onUpdateTicket?: (ticketId: number, updatedFields: Record<string, any>) => void;
}

const ChartMenuMultiSelect: React.FC<ChartMenuMultiSelectProps> = ({
  showMultiSelectMenu,
  selectedTickets,
  onSetRefresh,
  handleClose,
  onMoveAndExtendIteration,
  RemoveFromTimeline,
  onUpdateTicket,
}) => {
  const [move, setMove] = useState<number>(0);
  const [length, setLength] = useState<number>(0);
  const [bulkPlanningPriority, setBulkPlanningPriority] = useState<number | null>(null);
  const [bulkInitiative, setBulkInitiative] = useState<string>("");
  const [bulkPlanningStatus, setBulkPlanningStatus] = useState<string>("");

  const close = (refresh = false) => {
    if (refresh) {
      onSetRefresh();
    }
    setMove(0);
    setLength(0);
    setBulkPlanningPriority(null);
    setBulkInitiative("");
    setBulkPlanningStatus("");
    handleClose();
  };

  const handleBulkMove = () => {
    if (move !== 0 || length !== 0) {
      selectedTickets.forEach(ticket => {
        onMoveAndExtendIteration?.(ticket, move * 14, length * 14);
      });
      close(true);
    }
  };

  const handleBulkRemove = () => {
    selectedTickets.forEach(ticket => {
      RemoveFromTimeline(ticket.id);
    });
    close(true);
  };

  const handleMoveToQuarter = (direction: 'prev' | 'next') => {
    selectedTickets.forEach(ticket => {
      const startIteration = moment(ticket.fields[AF_START_ITERATION]);
      const newStartDate = direction === 'prev' 
        ? findPrevQuarterStart(startIteration.toDate())
        : findNextQuarterStart(startIteration.toDate());
      const moveNumberOfDays = moment(newStartDate).diff(startIteration, "days");
      
      onMoveAndExtendIteration?.(ticket, moveNumberOfDays + (direction === 'next' ? 1 : 0), 0);
    });
    close(true);
  };

  const handleBulkFieldUpdate = () => {
    const hasFieldChanges = bulkPlanningPriority !== null || bulkInitiative !== "" || bulkPlanningStatus !== "";
    const hasMoveChanges = move !== 0 || length !== 0;
    
    if (hasFieldChanges || hasMoveChanges) {
      if (onUpdateTicket && (hasFieldChanges || hasMoveChanges)) {
        
        selectedTickets.forEach(ticket => {
        const fieldsToUpdate: any = {};
        
            if (bulkPlanningPriority !== null) fieldsToUpdate[AF_PLANNING_PRIORITY] = bulkPlanningPriority;
            if (bulkInitiative !== "") fieldsToUpdate[AF_INITIATIV] = bulkInitiative;
            if (bulkPlanningStatus !== "") fieldsToUpdate[AF_PLANNING_STATUS] = bulkPlanningStatus;
            const newStartDate = new Date(new Date(ticket.startIteration).getTime() + move * 14 * 24 * 60 * 60 * 1000);
            const newEndDate = new Date(new Date(ticket.endIteration).getTime() + (move + length) * 14 * 24 * 60 * 60 * 1000);
            if (move !== 0) fieldsToUpdate[AF_START_ITERATION] = moment(newStartDate).format("YYYY-MM-DD");
            if (move !== 0 || length !== 0) fieldsToUpdate[AF_END_ITERATION] = moment(newEndDate).format("YYYY-MM-DD");
          
          onUpdateTicket(ticket.id, fieldsToUpdate);
        });
      }
      
      close(true);
    }
  };

  if (!showMultiSelectMenu || selectedTickets.length === 0) {
    return null;
  }

  return (
    <Modal
      visible={showMultiSelectMenu}
      onCancel={() => close()}
      footer={null}
      width="60%"
      title={`Bulk Operations (${selectedTickets.length} items selected)`}
    >
      <div>
        <h3>Selected Items:</h3>
        <div style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '20px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #d9d9d9' }}>
                <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>ID</th>
                <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>Title</th>
                <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>Priority</th>
                <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>Initiative</th>
                <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {selectedTickets.map(ticket => (
                <tr key={ticket.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '4px', fontWeight: '500' }}>{ticket.id}</td>
                  <td style={{ padding: '4px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.fields[AF_TITLE]}
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    {ticket.fields[AF_PLANNING_PRIORITY] || '-'}
                  </td>
                  <td style={{ padding: '4px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.fields[AF_INITIATIV] || '-'}
                  </td>
                  <td style={{ padding: '4px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.fields[AF_PLANNING_STATUS] || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <h4 style={{ margin: "0 0 8px 0" }}>Timeline Operations</h4>
        <div style={{ backgroundColor: "#F1F2F3", padding: "10px", borderRadius: "5px", marginBottom: "16px" }}>
          <Tooltip title="Remove all from Timeline">
            <Button
              type="primary"
              size="small"
              children={<DeleteRowOutlined />}
              onClick={handleBulkRemove}
            />
          </Tooltip>

          <Divider type="vertical" />

          <label>Move: </label>
          <InputNumber
            min={-12}
            max={12}
            value={move}
            onChange={(value: any) => setMove(value || 0)}
          />
          &nbsp;&nbsp;

          <label>Extend: </label>
          <InputNumber
            min={-12}
            max={12}
            value={length}
            onChange={(value: any) => setLength(value || 0)}
          />
          &nbsp;&nbsp;

          <Tooltip title="Update period and length for all selected">
            <Button
              type="primary"
              size="small"
              children={<CheckOutlined />}
              onClick={handleBulkMove}
            />
          </Tooltip>

          <Divider type="vertical" />
          
          <Tooltip title="Move all to Previous Quarter">
            <Button
              type="primary"
              size="small"
              children={<FastBackwardOutlined />}
              onClick={() => handleMoveToQuarter('prev')}
            />
          </Tooltip>

          <Tooltip title="Move all to Next Quarter">
            <Button
              type="primary"
              size="small"
              children={<FastForwardOutlined />}
              onClick={() => handleMoveToQuarter('next')}
            />
          </Tooltip>
        </div>
        
        <div style={{ backgroundColor: "#F8F9FA", padding: "12px", borderRadius: "5px", marginBottom: "16px" }}>
          <h4 style={{ margin: "0 0 12px 0" }}>Bulk Field Updates</h4>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Planning Priority:</label>
              <InputNumber
                size="small"
                value={bulkPlanningPriority}
                onChange={(value: number | null) => setBulkPlanningPriority(value || null)}
                min={-100}
                max={100000}
              />
            </div>
            
            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Planning Status:</label>
              <Select
                size="small"
                value={bulkPlanningStatus || undefined}
                onChange={(value) => setBulkPlanningStatus(value || "")}
                placeholder="Set status..."
                style={{ width: "100%" }}
                allowClear
              >
                {ADO_PLANNING_STATUSES.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Initiative:</label>
            <Input
              value={bulkInitiative}
              onChange={(e) => setBulkInitiative(e.target.value)}
              placeholder="Set initiative name..."
              style={{ 
                width: "100%"
              }}
            />
          </div>
          
        </div>
      </div>
      
      <div style={{ textAlign: 'right', marginTop: '20px' }}>
        {(bulkPlanningPriority !== null || bulkInitiative !== "" || bulkPlanningStatus !== "" || move !== 0 || length !== 0) ? (
          <>
            <Button onClick={() => close()}>
              Cancel
            </Button>
            &nbsp;
            <Button 
              type="primary" 
              onClick={handleBulkFieldUpdate}
            >
              <SaveOutlined /> Save
            </Button>
          </>
        ) : (
          <Button type="primary" onClick={() => close()}>
            Close
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default ChartMenuMultiSelect;
